import { useEffect, useMemo, useState } from 'react'
import { DepositeTypesResponse } from '@http/api-models/DepositeTypesResponse'
import { apiUrls } from '@http/api-urls'
import { httpService } from '@http/service'
import { consoleDebug, isDebug } from '@shared/pipes/is-debug'
import type {
	SavingCalculatorProgram,
	SavingCalculatorProgramsResponse,
} from '@http/api-models/SavingPrograms'

const sendCalcSavingsForm = ({ amount }: { amount: number }) => {
	if (isDebug()) {
		consoleDebug('[Loan form] - prevent send form')
	}

	return httpService.get<SavingCalculatorProgramsResponse>(apiUrls.getSavingPrograms({ amount }))
}

const MIN_STEP = 1 // начальное значение range-input
const MAX_STEP = 591 // количество всех чисел (1 тыс., 5 тыс., 10 тыс., 100тыс и т.д.)
const MIN_AMOUNT = 10000
const THOUSANDS_100 = 100000
const MILLION_1 = 1000000
const MAX_AMOUNT = MILLION_1 * 40

// получение значения range-input из суммы
export const getStepsFromAmount = (amount: number) => {
	let tempAmount = amount
	let currentStep = 0

	while (tempAmount > 0) {
		if (tempAmount >= 1100000) {
			tempAmount -= 100000
			currentStep++
			continue
		}
		if (tempAmount > 1000000 && tempAmount < 1100000) {
			tempAmount = 1000000
		}

		if (tempAmount >= 310000) {
			tempAmount -= 10000
			currentStep++
			continue
		}

		if (tempAmount > 300000 && tempAmount < 310000) {
			tempAmount = 300000
		}

		if (tempAmount >= 105000) {
			tempAmount -= 5000
			currentStep++
			continue
		}
		if (tempAmount > 100000 && tempAmount < 105000) {
			tempAmount = 100000
		}

		if (tempAmount <= 11000 && tempAmount >= 10000) {
			tempAmount -= 10000
			currentStep++

			if (tempAmount < 1000) {
				break
			}
		}

		if (tempAmount >= 1000) {
			tempAmount -= 1000
			currentStep++

			if (tempAmount < 1000) {
				break
			}
		}
	}

	return currentStep
}

// получение сумму из значения range-input
export const getAmountFromSteps = (steps: number) => {
	let amount = 0

	for (let i = 0; i < steps; i++) {
		if (i === 0) {
			amount += 10000
			continue
		}
		if (i < 91) {
			// до 100000
			amount += 1000
			continue
		}
		if (i < 131) {
			// до 300000
			amount += 5000
			continue
		}
		if (i <= 200) {
			// до 1000000
			amount += 10000
			continue
		}

		amount += 100000 // до 5000000
	}

	return amount
}

interface UseCalcValue {
	rangeValue: number
	min: number
	max: number
	handleChange: (value: number) => void
	programs: [Array<SavingCalculatorProgram>, Array<SavingCalculatorProgram>]
}

export function useCalc({
	initialPrograms,
}: {
	initialPrograms: SavingCalculatorProgramsResponse
}): UseCalcValue {
	const [amount, setAmount] = useState<number>(MIN_AMOUNT)
	const [rangeValue, setRangeValue] = useState<number>(1)
	const [programs, setPrograms] = useState<SavingCalculatorProgramsResponse>(
		[...initialPrograms].sort((a, b) => b.annualProfitPercentage - a.annualProfitPercentage)
	)

	const handleChange = (steps: number) => {
		setRangeValue(steps)
		setAmount(getAmountFromSteps(steps))
	}

	useEffect(() => {
		if (amount < MIN_AMOUNT) {
			setAmount(MIN_AMOUNT)
		} else if (amount > MAX_AMOUNT) {
			setAmount(MAX_AMOUNT)
		}
	}, [amount])

	useEffect(() => {
		const updater = async () => {
			const result = await sendCalcSavingsForm({
				amount,
			})

			setPrograms([...result].sort((a, b) => b.annualProfitPercentage - a.annualProfitPercentage))
		}

		const timeout = setTimeout(updater, 250)

		return () => {
			clearTimeout(timeout)
		}
	}, [amount])

	const sortedPrograms = useMemo(() => {
		return programs.reduce(
			(acc, program) => {
				if (program.isAvailableForNewClient) {
					acc[0].push(program)
				} else {
					acc[1].push(program)
				}

				return acc
			},
			[[], []] as [Array<SavingCalculatorProgram>, Array<SavingCalculatorProgram>]
		)
	}, [programs])

	const result = useMemo<UseCalcValue>(
		() => ({
			min: MIN_STEP,
			max: MAX_STEP,
			rangeValue,
			handleChange,
			programs: sortedPrograms,
		}),
		[sortedPrograms, amount]
	)
	return result
}
